import type { Product, ProductVariantItem } from '../types';

type GtmItem = {
  item_id: string | number;
  item_name: string;
  item_category?: string;
  price: number;
  quantity: number;
};

type Ecommerce = {
  currency: 'GBP';
  value: number;
  payment_type?: 'Online' | 'Wallet';
  transaction_id?: string | number;
  items: GtmItem[];
};

type PaymentMethod = 'credit' | 'zarinpal';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const ZARINPAL_STASH_PREFIX = 'gtm:zarinpalCheckout:';
const PENDING_LOGIN_KEY = 'gtm:pendingLogin';
const PENDING_WITHDRAWALS_KEY = 'gtm:pendingWithdrawals';

function pushEcommerce(event: string, ecommerce: Ecommerce) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce });
}

function pushEvent(event: string, params?: Record<string, unknown>) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...(params ?? {}) });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function buildItem(product: Product, variant?: ProductVariantItem): GtmItem {
  return {
    item_id: variant ? variant.id : product.id,
    item_name: variant ? `${product.title} - ${variant.title}` : product.title,
    item_category: product.type,
    price: Number(variant ? variant.price : product.price),
    quantity: 1,
  };
}

function baseValue(product: Product, variant?: ProductVariantItem): number {
  return Number(variant ? variant.price : product.price);
}

function finalValue(
  base: number,
  paymentMethod: PaymentMethod,
  onlineTaxPercent: number
): number {
  if (paymentMethod === 'zarinpal') {
    return round2(base * (1 + onlineTaxPercent / 100));
  }
  return base;
}

function paymentType(paymentMethod: PaymentMethod): 'Online' | 'Wallet' {
  return paymentMethod === 'zarinpal' ? 'Online' : 'Wallet';
}

export function trackViewItem(params: {
  product: Product;
  variant?: ProductVariantItem;
}): void {
  const { product, variant } = params;
  pushEcommerce('view_item', {
    currency: 'GBP',
    value: baseValue(product, variant),
    items: [buildItem(product, variant)],
  });
}

export function trackAddToCart(params: {
  product: Product;
  variant?: ProductVariantItem;
}): void {
  const { product, variant } = params;
  pushEcommerce('add_to_cart', {
    currency: 'GBP',
    value: baseValue(product, variant),
    items: [buildItem(product, variant)],
  });
}

export function trackBeginCheckout(params: {
  product: Product;
  variant?: ProductVariantItem;
}): void {
  const { product, variant } = params;
  pushEcommerce('begin_checkout', {
    currency: 'GBP',
    value: baseValue(product, variant),
    items: [buildItem(product, variant)],
  });
}

export function trackAddPaymentInfo(params: {
  product: Product;
  variant?: ProductVariantItem;
  paymentMethod: PaymentMethod;
  onlineTaxPercent: number;
}): void {
  const { product, variant, paymentMethod, onlineTaxPercent } = params;
  const base = baseValue(product, variant);
  pushEcommerce('add_payment_info', {
    currency: 'GBP',
    value: finalValue(base, paymentMethod, onlineTaxPercent),
    payment_type: paymentType(paymentMethod),
    items: [buildItem(product, variant)],
  });
}

export function trackPurchase(params: {
  product: Product;
  variant?: ProductVariantItem;
  paymentMethod: PaymentMethod;
  transactionId: string | number;
  onlineTaxPercent: number;
}): void {
  const { product, variant, paymentMethod, transactionId, onlineTaxPercent } = params;
  const base = baseValue(product, variant);
  pushEcommerce('purchase', {
    currency: 'GBP',
    value: finalValue(base, paymentMethod, onlineTaxPercent),
    payment_type: paymentType(paymentMethod),
    transaction_id: transactionId,
    items: [buildItem(product, variant)],
  });
}

export function trackLogin(method: 'phone' | 'google'): void {
  try {
    sessionStorage.setItem(PENDING_LOGIN_KEY, method);
  } catch {
    // Analytics must never block login.
  }
}

export function trackPendingLogin(params: {
  is_youber_creator: boolean;
  monthly_income_gbp: number;
  is_eligible_for_early_income: boolean;
}): void {
  try {
    const method = sessionStorage.getItem(PENDING_LOGIN_KEY);
    if (!method) return;
    pushEvent('login', { method, ...params });
    sessionStorage.removeItem(PENDING_LOGIN_KEY);
  } catch {
    // Analytics must never block the panel.
  }
}

export function trackSignup(method: 'phone' | 'google'): void {
  pushEvent('sign_up', { method });
}

type WithdrawEvent = {
  transaction_id?: string | number;
  currency: 'IRT' | 'USDT';
  withdraw_amount: number;
  available_balance: number;
  early_income_credit: number;
  used_early_income: boolean;
  early_income_used_amount: number;
};

function pendingWithdrawals(): Record<string, WithdrawEvent> {
  try {
    return JSON.parse(localStorage.getItem(PENDING_WITHDRAWALS_KEY) || '{}');
  } catch {
    return {};
  }
}

export function trackWithdrawRequest(params: WithdrawEvent): void {
  pushEvent('withdraw_request', params);
  if (params.transaction_id == null) return;
  try {
    localStorage.setItem(PENDING_WITHDRAWALS_KEY, JSON.stringify({
      ...pendingWithdrawals(),
      [params.transaction_id]: params,
    }));
  } catch {
    // The request event is still useful when persistence is unavailable.
  }
}

export function trackCompletedWithdrawals(
  transactions: Array<{ id: string | number; type: string; status: string }>
): void {
  const pending = pendingWithdrawals();
  let changed = false;
  transactions.forEach(({ id, type, status }) => {
    if (type !== 'WITHDRAW' || !pending[id]) return;
    if (status === 'SUCCESS') {
      pushEvent('withdraw_completed', pending[id]);
      delete pending[id];
      changed = true;
    } else if (status === 'CANCELLED') {
      delete pending[id];
      changed = true;
    }
  });
  if (changed) {
    try {
      localStorage.setItem(PENDING_WITHDRAWALS_KEY, JSON.stringify(pending));
    } catch {
      // Analytics persistence is best-effort.
    }
  }
}

type ZarinpalCheckoutStash = {
  product: Product;
  variant?: ProductVariantItem;
  onlineTaxPercent: number;
};

export function stashZarinpalCheckout(
  orderId: string | number,
  data: ZarinpalCheckoutStash
): void {
  try {
    sessionStorage.setItem(
      `${ZARINPAL_STASH_PREFIX}${orderId}`,
      JSON.stringify(data)
    );
  } catch {
    // sessionStorage unavailable; skip silently
  }
}

export function readZarinpalCheckoutStash(
  orderId: string | number
): ZarinpalCheckoutStash | null {
  try {
    const raw = sessionStorage.getItem(`${ZARINPAL_STASH_PREFIX}${orderId}`);
    return raw ? (JSON.parse(raw) as ZarinpalCheckoutStash) : null;
  } catch {
    return null;
  }
}

export function clearZarinpalCheckoutStash(orderId: string | number): void {
  try {
    sessionStorage.removeItem(`${ZARINPAL_STASH_PREFIX}${orderId}`);
  } catch {
    // sessionStorage unavailable; skip silently
  }
}
